import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, ListGroup, Spinner, Alert, InputGroup, Modal } from "react-bootstrap";
import { FaCalendarDay, FaPlus, FaTrash, FaSun, FaListUl, FaBriefcase, FaCheckCircle, FaBan, FaLeaf, FaUtensils, FaArrowRight, FaDumbbell, FaBolt, FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight, FaEye, FaBook, FaSave, FaFilePdf } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Link } from "react-router-dom";
import { api } from "../../contexts/AuthContext";

const SIDEBAR_BLUE = "#60A5FA";
const DO_GREEN = "#34D399";
const DONT_RED = "#F87171";

const WALK_20 = "Walk 20'";
const COLD_SHOWER = "Cold Shower";

const SHARED_GROWTH_ITEMS = [
  "Listen to affirmations",
  "Watch motivational videos",
  "Read a book for 20'",
];

const DEFAULT_PRESETS = {
  morning: [
    "Brush teeth",
    "Weigh myself",
    "Drink water",
    "Make coffee",
    "Go to work",
  ],
  work: [
    "Commute to office",
    "Check emails",
    "Standup meeting",
    "Lunch",
    "Commute home",
    ...SHARED_GROWTH_ITEMS,
  ],
  workout: [
    WALK_20,
  ],
  day_core: [
    WALK_20,
    COLD_SHOWER,
    "Customer work",
    "Eat",
    "Mental exercises",
    ...SHARED_GROWTH_ITEMS,
  ],
  relaxation: [
    "Abdominal breathing",
    "Progressive muscle relaxation",
    "Mindfulness meditation",
    "Yoga",
    "Visualization",
    "Deep breathing",
  ],
  optional: [
    "Drink 2–3L water",
    "Get some sunshine",
    "Stretch for 5 minutes",
    "Take a short walk",
  ],
  avoid: [
    "Don't gamble",
    "Don't drink alcohol",
    "Don't eat too much sugar",
    "Don't doomscroll",
  ],
  exposure: [],
};

const SECTION_LABELS = {
  morning: "Saved morning items",
  work: "Saved work items",
  workout: "Saved workout items",
  day_core: "Saved Day Core items",
  relaxation: "Saved relaxation methods",
  optional: "Saved optional items",
  avoid: "Saved avoid items",
  exposure: "Saved exposure items",
};

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isWeekday = (date) => {
  const day = date.getDay();
  return day >= 1 && day <= 5;
};

const isWeekend = (date) => !isWeekday(date);

const needsResult = (title) => /weigh|weight|scale/i.test(title);
const needsBookNote = (title) => /read a book/i.test(title);
const showsResultField = (task) => needsResult(task.title) || needsBookNote(task.title) || !!task.result;

const RoutineSection = ({
  title,
  icon,
  description,
  section,
  tasks,
  presets,
  saving,
  onAddTask,
  onDeleteTask,
  onToggleCompleted,
  onResultChange,
  onWeightChange,
  onResultBlur,
  onOpenPresets,
  extraContent,
}) => {
  const [newTaskTitle, setNewTaskTitle] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = newTaskTitle.trim();
    if (!value) return;
    await onAddTask(section, value);
    setNewTaskTitle("");
  };

  return (
    <Card
      className="mb-4"
      style={{
        backgroundColor: "#1e1e1e",
        border: `1px solid ${SIDEBAR_BLUE}`,
      }}
    >
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          {icon}
          <h4 className="mb-0" style={{ color: "#fff" }}>{title}</h4>
        </div>

        <p className="mb-3" style={{ fontSize: "0.9rem", color: "#d1d5db" }}>
          {description}
        </p>

        {extraContent}

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            onClick={() => onOpenPresets(section)}
            style={{
              backgroundColor: "transparent",
              border: `1px solid ${SIDEBAR_BLUE}`,
              color: SIDEBAR_BLUE,
            }}
          >
            <FaListUl className="me-2" />
            From list ({presets.length})
          </Button>
        </div>

        <Form onSubmit={handleSubmit} className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              className="day-builder-input"
              placeholder="Type a new item..."
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              style={{
                backgroundColor: "#2d2d2d",
                border: `1px solid ${SIDEBAR_BLUE}`,
                color: "#fff",
              }}
            />
            <Button
              type="submit"
              disabled={!newTaskTitle.trim() || saving}
              style={{
                backgroundColor: SIDEBAR_BLUE,
                borderColor: SIDEBAR_BLUE,
              }}
            >
              <FaPlus className="me-1" />
              Add
            </Button>
          </InputGroup>
        </Form>

        {tasks.length === 0 ? (
          <p className="mb-0" style={{ fontSize: "0.9rem", color: "#d1d5db" }}>
            No tasks for this section yet. Open the list or type a new one.
          </p>
        ) : (
          <ListGroup variant="flush">
            {tasks.map((task, index) => (
              <ListGroup.Item
                key={task.id}
                style={{
                  backgroundColor: "#2d2d2d",
                  border: `1px solid ${SIDEBAR_BLUE}`,
                  borderRadius: "4px",
                  marginBottom: "8px",
                  color: "#fff",
                }}
              >
                <div className="d-flex justify-content-between align-items-center gap-2">
                  <Form.Check
                    type="checkbox"
                    checked={!!task.is_completed}
                    onChange={() => onToggleCompleted(task)}
                    label={
                      <span style={{
                        color: "#fff",
                        textDecoration: task.is_completed ? "line-through" : "none",
                        opacity: task.is_completed ? 0.7 : 1,
                      }}>
                        <span className="me-2" style={{ color: "#9ca3af" }}>{index + 1}.</span>
                        {task.title}
                      </span>
                    }
                  />
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => onDeleteTask(task.id)}
                    style={{ color: "#F87171", textDecoration: "none" }}
                    title="Remove task"
                  >
                    <FaTrash />
                  </Button>
                </div>

                {showsResultField(task) && (
                  <Form.Control
                    type={needsResult(task.title) ? "number" : "text"}
                    className="day-builder-input mt-2"
                    placeholder={
                      needsResult(task.title)
                        ? "Weight (35–200 kg)"
                        : needsBookNote(task.title)
                          ? "Book, chapter, pages..."
                          : "Result (optional)"
                    }
                    value={task.result || ""}
                    min={needsResult(task.title) ? 35 : undefined}
                    max={needsResult(task.title) ? 200 : undefined}
                    step={needsResult(task.title) ? "0.1" : undefined}
                    disabled={needsResult(task.title) ? !task.is_completed : false}
                    onChange={(e) =>
                      needsResult(task.title)
                        ? onWeightChange(task.id, e.target.value)
                        : onResultChange(task.id, e.target.value)
                    }
                    onBlur={() => {
                      if (needsResult(task.title) && !task.is_completed) return;
                      onResultBlur(task);
                    }}
                    style={{
                      backgroundColor: needsResult(task.title) && !task.is_completed ? "#161616" : "#1e1e1e",
                      border: `1px solid ${SIDEBAR_BLUE}`,
                      color: needsResult(task.title) && !task.is_completed ? "#6b7280" : "#fff",
                      maxWidth: needsBookNote(task.title) ? "100%" : "220px",
                      opacity: needsResult(task.title) && !task.is_completed ? 0.6 : 1,
                    }}
                  />
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  );
};

const RELAX_ACCENT = "#A78BFA";

const JOURNAL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote"],
    ["clean"],
  ],
};

const JOURNAL_FORMATS = [
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "blockquote",
];

const RelaxationPicker = ({
  options,
  selected,
  completed,
  saving,
  onSelect,
  onToggleCompleted,
  onAddOption,
  onDeleteOption,
}) => {
  const [newOption, setNewOption] = useState("");
  const [choosing, setChoosing] = useState(!selected);

  useEffect(() => {
    setChoosing(!selected);
  }, [selected]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const title = newOption.trim();
    if (!title) return;
    await onAddOption(title);
    setNewOption("");
  };

  const handleSelect = async (title) => {
    await onSelect(title);
    setChoosing(false);
  };

  return (
    <div
      className="mb-4"
      style={{
        backgroundColor: "#2d2d2d",
        border: `1px solid ${RELAX_ACCENT}`,
        borderRadius: "4px",
        padding: "12px 14px",
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center">
          <FaLeaf className="me-2" style={{ color: RELAX_ACCENT }} />
          <h5 className="mb-0" style={{ color: "#fff", fontSize: "1rem" }}>Relaxation</h5>
        </div>
        {selected && !choosing && (
          <Button
            size="sm"
            onClick={() => setChoosing(true)}
            style={{
              backgroundColor: "transparent",
              border: `1px solid ${RELAX_ACCENT}`,
              color: RELAX_ACCENT,
            }}
          >
            Change
          </Button>
        )}
      </div>

      {!choosing && selected ? (
        <Form.Check
          type="checkbox"
          id="relaxation-completed"
          checked={!!completed}
          disabled={saving}
          onChange={onToggleCompleted}
          label={
            <span
              style={{
                color: "#fff",
                textDecoration: completed ? "line-through" : "none",
                opacity: completed ? 0.7 : 1,
              }}
            >
              Today: <span style={{ color: RELAX_ACCENT }}>{selected}</span>
            </span>
          }
        />
      ) : (
        <>
          <p className="mb-3" style={{ fontSize: "0.85rem", color: "#d1d5db" }}>
            Pick one method for today. You can add more options anytime.
          </p>

          {options.length === 0 ? (
            <p className="mb-3" style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
              No relaxation methods yet. Add one below.
            </p>
          ) : (
            <div className="mb-3">
              {options.map((option) => (
                <div
                  key={option.id}
                  className="d-flex align-items-center justify-content-between mb-2"
                >
                  <Form.Check
                    type="radio"
                    name="day-core-relaxation"
                    id={`relax-${option.id}`}
                    checked={selected === option.title}
                    disabled={saving}
                    onChange={() => handleSelect(option.title)}
                    label={<span style={{ color: "#fff" }}>{option.title}</span>}
                  />
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => onDeleteOption(option.id)}
                    style={{ color: DONT_RED, textDecoration: "none" }}
                    title="Remove from options"
                  >
                    <FaTrash />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <Form onSubmit={handleAdd}>
            <InputGroup size="sm">
              <Form.Control
                type="text"
                className="day-builder-input"
                placeholder="Add another method..."
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                style={{
                  backgroundColor: "#1e1e1e",
                  border: `1px solid ${RELAX_ACCENT}`,
                  color: "#fff",
                }}
              />
              <Button
                type="submit"
                disabled={!newOption.trim() || saving}
                style={{
                  backgroundColor: RELAX_ACCENT,
                  borderColor: RELAX_ACCENT,
                  color: "#111",
                }}
              >
                <FaPlus />
              </Button>
            </InputGroup>
          </Form>
        </>
      )}
    </div>
  );
};

const GuidelineGroup = ({
  heading,
  icon,
  accent,
  hint,
  section,
  tasks,
  presets,
  saving,
  onDeleteTask,
  onOpenPresets,
  onAddTask,
}) => {
  const [newTitle, setNewTitle] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = newTitle.trim();
    if (!value) return;
    await onAddTask(section, value);
    setNewTitle("");
  };

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center">
          {icon}
          <h5 className="mb-0" style={{ color: accent }}>{heading}</h5>
        </div>
        <Button
          size="sm"
          onClick={() => onOpenPresets(section)}
          style={{
            backgroundColor: "transparent",
            border: `1px solid ${accent}`,
            color: accent,
          }}
        >
          <FaListUl className="me-1" />
          List ({presets.length})
        </Button>
      </div>

      <p className="mb-2" style={{ fontSize: "0.85rem", color: "#d1d5db" }}>{hint}</p>

      <Form onSubmit={handleSubmit} className="mb-3">
        <InputGroup size="sm">
          <Form.Control
            type="text"
            className="day-builder-input"
            placeholder="Add custom..."
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            style={{
              backgroundColor: "#2d2d2d",
              border: `1px solid ${accent}`,
              color: "#fff",
            }}
          />
          <Button
            type="submit"
            disabled={!newTitle.trim() || saving}
            style={{ backgroundColor: accent, borderColor: accent, color: "#111" }}
          >
            <FaPlus />
          </Button>
        </InputGroup>
      </Form>

      {tasks.length === 0 ? (
        <p className="mb-0" style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
          Nothing here yet — use List or add your own.
        </p>
      ) : (
        <ListGroup variant="flush">
          {tasks.map((task) => (
            <ListGroup.Item
              key={task.id}
              className="d-flex justify-content-between align-items-center"
              style={{
                backgroundColor: "#2d2d2d",
                border: `1px solid ${accent}`,
                borderRadius: "4px",
                marginBottom: "8px",
                color: "#fff",
                padding: "8px 12px",
              }}
            >
              <span style={{ color: "#fff" }}>{task.title}</span>
              <Button
                variant="link"
                size="sm"
                onClick={() => onDeleteTask(task.id)}
                style={{ color: DONT_RED, textDecoration: "none" }}
                title="Remove for today"
              >
                <FaTrash />
              </Button>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

const DayBuilder = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [plan, setPlan] = useState(null);
  const [presets, setPresets] = useState([]);
  const [presetSearch, setPresetSearch] = useState("");
  const [presetSection, setPresetSection] = useState("morning");
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [journal, setJournal] = useState("");
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalDirty, setJournalDirty] = useState(false);
  const [journalSavedAt, setJournalSavedAt] = useState(null);
  const [dayMeals, setDayMeals] = useState([]);
  const [daySupplements, setDaySupplements] = useState([]);
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [workoutDayLog, setWorkoutDayLog] = useState(null);
  const [workoutMeta, setWorkoutMeta] = useState({ rest_day: false, plan: null, session: null, message: "" });
  const [workoutLoading, setWorkoutLoading] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const showWork = isWeekday(selectedDate);
  const morningTasks = (plan?.tasks || []).filter((task) => task.section === "morning");
  const workTasks = (plan?.tasks || [])
    .filter((task) => task.section === "work")
    .sort((a, b) => a.order - b.order || a.id - b.id);
  const dayCoreTasks = (plan?.tasks || [])
    .filter((task) => task.section === "day_core")
    .sort((a, b) => a.order - b.order || a.id - b.id);
  const optionalTasks = (plan?.tasks || []).filter((task) => task.section === "optional");
  const avoidTasks = (plan?.tasks || []).filter((task) => task.section === "avoid");
  const exposureTasks = (plan?.tasks || []).filter((task) => task.section === "exposure");
  const morningPresets = presets.filter((preset) => preset.section === "morning");
  const workPresets = presets.filter((preset) => preset.section === "work");
  const dayCorePresets = presets.filter((preset) => preset.section === "day_core");
  const relaxationPresets = presets.filter((preset) => preset.section === "relaxation");
  const optionalPresets = presets.filter((preset) => preset.section === "optional");
  const avoidPresets = presets.filter((preset) => preset.section === "avoid");
  const exposurePresets = presets.filter((preset) => preset.section === "exposure");
  const activePresets = presets.filter((preset) => preset.section === presetSection);
  const activeTasks = (plan?.tasks || []).filter((task) => task.section === presetSection);
  const addedTitles = new Set(activeTasks.map((task) => task.title.toLowerCase()));
  const filteredPresets = activePresets.filter((preset) =>
    preset.title.toLowerCase().includes(presetSearch.trim().toLowerCase())
  );

  useEffect(() => {
    loadPresets();
    // Intentional: seed presets once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPlan(selectedDate);
    loadDayNutrition(selectedDate);
    loadDayWorkout(selectedDate);
    // Intentional: reload when the selected date changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    setJournal(plan?.journal || "");
    setJournalDirty(false);
    setJournalSavedAt(null);
    // Sync when switching days/plans only — not on every journal field echo after save
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id]);

  const shiftDate = ({ days = 0, months = 0 }) => {
    setSelectedDate((current) => {
      const next = new Date(current);
      if (months) {
        next.setMonth(next.getMonth() + months);
      }
      if (days) {
        next.setDate(next.getDate() + days);
      }
      return next;
    });
  };

  const ensureDefaultPresets = async (section, existing) => {
    if (existing.length > 0) return existing;
    const defaults = DEFAULT_PRESETS[section] || [];
    if (defaults.length === 0) return [];
    const created = await Promise.all(
      defaults.map((title) =>
        api.post("/api/routine-presets/", { title, section })
      )
    );
    return created.map((res) => res.data);
  };

  const loadPresets = async () => {
    try {
      const response = await api.get("/api/routine-presets/");
      const items = response.data.results || response.data;

      const morning = items.filter((item) => item.section === "morning");
      const work = items.filter((item) => item.section === "work");
      const workout = items.filter((item) => item.section === "workout");
      const dayCore = items.filter((item) => item.section === "day_core");
      const relaxation = items.filter((item) => item.section === "relaxation");
      const optional = items.filter((item) => item.section === "optional");
      const avoid = items.filter((item) => item.section === "avoid");

      const seededMorning = await ensureDefaultPresets("morning", morning);
      const seededWork = await ensureDefaultPresets("work", work);
      const seededWorkout = await ensureDefaultPresets("workout", workout);
      const seededDayCore = await ensureDefaultPresets("day_core", dayCore);
      const seededRelaxation = await ensureDefaultPresets("relaxation", relaxation);
      const seededOptional = await ensureDefaultPresets("optional", optional);
      const seededAvoid = await ensureDefaultPresets("avoid", avoid);

      const other = items.filter(
        (item) => !["morning", "work", "workout", "day_core", "relaxation", "optional", "avoid", "exposure"].includes(item.section)
      );
      setPresets([
        ...seededMorning,
        ...seededWork,
        ...seededWorkout,
        ...seededDayCore,
        ...seededRelaxation,
        ...seededOptional,
        ...seededAvoid,
        ...other,
      ]);
    } catch (err) {
      console.error("Failed to load presets:", err);
    }
  };

  const seedGuideTasks = async (planData, allPresets) => {
    const existing = planData.tasks || [];
    const hasOptional = existing.some((task) => task.section === "optional");
    const hasAvoid = existing.some((task) => task.section === "avoid");
    if (hasOptional && hasAvoid) return planData;

    const createdTasks = [];

    if (!hasOptional) {
      const optional = allPresets.filter((preset) => preset.section === "optional");
      for (const preset of optional) {
        const res = await api.post("/api/day-plan-tasks/", {
          plan: planData.id,
          title: preset.title,
          section: "optional",
        });
        createdTasks.push(res.data);
      }
    }

    if (!hasAvoid) {
      const avoid = allPresets.filter((preset) => preset.section === "avoid");
      for (const preset of avoid) {
        const res = await api.post("/api/day-plan-tasks/", {
          plan: planData.id,
          title: preset.title,
          section: "avoid",
        });
        createdTasks.push(res.data);
      }
    }

    if (createdTasks.length === 0) return planData;
    return {
      ...planData,
      tasks: [...existing, ...createdTasks],
    };
  };

  const ensurePresetsExist = async (section, titles, allPresets) => {
    const existing = allPresets.filter((preset) => preset.section === section);
    const created = [];

    for (const title of titles) {
      const already = existing.some(
        (preset) => preset.title.toLowerCase() === title.toLowerCase()
      );
      if (already) continue;
      try {
        const res = await api.post("/api/routine-presets/", { title, section });
        created.push(res.data);
      } catch (err) {
        // May already exist from a race — ignore
      }
    }

    return [...allPresets, ...created];
  };

  const ensureDayTasks = async (planData, section, titles) => {
    const existing = planData.tasks || [];
    const sectionTasks = existing.filter((task) => task.section === section);
    const createdTasks = [];

    for (const title of titles) {
      const already = sectionTasks.some(
        (task) => task.title.toLowerCase() === title.toLowerCase()
      );
      if (already) continue;
      const res = await api.post("/api/day-plan-tasks/", {
        plan: planData.id,
        title,
        section,
      });
      createdTasks.push(res.data);
    }

    if (createdTasks.length === 0) return planData;
    return {
      ...planData,
      tasks: [...existing, ...createdTasks],
    };
  };

  const ensurePinnedDayTasks = async (planData, section, pinnedTitles) => {
    const existing = planData.tasks || [];
    const sectionTasks = existing.filter((task) => task.section === section);
    const otherTasks = sectionTasks.filter(
      (task) =>
        !pinnedTitles.some(
          (title) => title.toLowerCase() === task.title.toLowerCase()
        )
    );

    const pinnedTasks = [];
    for (let i = 0; i < pinnedTitles.length; i++) {
      const title = pinnedTitles[i];
      let task = sectionTasks.find(
        (item) => item.title.toLowerCase() === title.toLowerCase()
      );

      if (!task) {
        const res = await api.post("/api/day-plan-tasks/", {
          plan: planData.id,
          title,
          section,
          order: i,
        });
        task = res.data;
      } else if (task.order !== i) {
        const res = await api.patch(`/api/day-plan-tasks/${task.id}/`, { order: i });
        task = res.data;
      }
      pinnedTasks.push(task);
    }

    const reorderedOthers = [];
    for (let i = 0; i < otherTasks.length; i++) {
      const newOrder = pinnedTitles.length + i;
      let task = otherTasks[i];
      if (task.order !== newOrder) {
        const res = await api.patch(`/api/day-plan-tasks/${task.id}/`, {
          order: newOrder,
        });
        task = res.data;
      }
      reorderedOthers.push(task);
    }

    const nonSectionTasks = existing.filter((task) => task.section !== section);
    return {
      ...planData,
      tasks: [...nonSectionTasks, ...pinnedTasks, ...reorderedOthers],
    };
  };

  const loadPlan = async (date) => {
    try {
      setLoading(true);
      setError(null);
      const dateStr = formatDate(date);
      const response = await api.get(`/api/day-plans/?date=${dateStr}`);
      const plans = response.data.results || response.data;

      let planData;
      if (plans.length > 0) {
        planData = plans[0];
      } else {
        const created = await api.post("/api/day-plans/", { date: dateStr });
        planData = created.data;
      }

      // Ensure guide presets exist before seeding the day
      const presetRes = await api.get("/api/routine-presets/");
      let allPresets = presetRes.data.results || presetRes.data;
      const optional = allPresets.filter((item) => item.section === "optional");
      const avoid = allPresets.filter((item) => item.section === "avoid");
      const seededOptional = await ensureDefaultPresets("optional", optional);
      const seededAvoid = await ensureDefaultPresets("avoid", avoid);
      allPresets = [
        ...allPresets.filter((item) => item.section !== "optional" && item.section !== "avoid"),
        ...seededOptional,
        ...seededAvoid,
      ];

      // Growth items: weekends → Day Core, weekdays → Work
      const growthSection = isWeekend(date) ? "day_core" : "work";
      allPresets = await ensurePresetsExist(growthSection, SHARED_GROWTH_ITEMS, allPresets);
      allPresets = await ensurePresetsExist("day_core", [WALK_20, COLD_SHOWER], allPresets);

      planData = await seedGuideTasks(planData, allPresets);
      planData = await ensureDayTasks(planData, growthSection, SHARED_GROWTH_ITEMS);

      // Walk 20' + Cold Shower placement/order
      if (isWeekend(date)) {
        planData = await ensurePinnedDayTasks(planData, "day_core", [WALK_20, COLD_SHOWER]);
      } else {
        planData = await ensurePinnedDayTasks(planData, "day_core", [COLD_SHOWER]);
      }

      setPlan(planData);
      setPresets((prev) => {
        const byKey = new Map(prev.map((p) => [`${p.section}:${p.title.toLowerCase()}`, p]));
        allPresets.forEach((p) => byKey.set(`${p.section}:${p.title.toLowerCase()}`, p));
        return Array.from(byKey.values());
      });
    } catch (err) {
      console.error("Failed to load day plan:", err);
      setError("Could not load this day's plan.");
      setPlan(null);
    } finally {
      setLoading(false);
    }
  };

  const loadDayNutrition = async (date) => {
    try {
      setNutritionLoading(true);
      const dateStr = formatDate(date);
      const [mealsRes, dosesRes] = await Promise.all([
        api.get("/api/nutrition-meals/", { params: { date: dateStr } }),
        api.get("/api/supplement-doses/", { params: { date: dateStr } }),
      ]);
      const meals = mealsRes.data.results || mealsRes.data;
      const doses = dosesRes.data.results || dosesRes.data;
      setDayMeals(Array.isArray(meals) ? meals : []);
      setDaySupplements(Array.isArray(doses) ? doses : []);
    } catch (err) {
      console.error("Failed to load day nutrition:", err);
      setDayMeals([]);
      setDaySupplements([]);
    } finally {
      setNutritionLoading(false);
    }
  };

  const handleToggleMealEaten = async (meal) => {
    const is_eaten = !meal.is_eaten;
    setDayMeals((prev) =>
      prev.map((item) => (item.id === meal.id ? { ...item, is_eaten } : item))
    );
    try {
      await api.patch(`/api/nutrition-meals/${meal.id}/`, { is_eaten });
    } catch (err) {
      console.error(err);
      setDayMeals((prev) =>
        prev.map((item) => (item.id === meal.id ? { ...item, is_eaten: meal.is_eaten } : item))
      );
      setError("Could not update meal eaten status.");
    }
  };

  const handleToggleSupplementTaken = async (dose) => {
    const is_taken = !dose.is_taken;
    setDaySupplements((prev) =>
      prev.map((item) => (item.id === dose.id ? { ...item, is_taken } : item))
    );
    try {
      await api.patch(`/api/supplement-doses/${dose.id}/`, { is_taken });
    } catch (err) {
      console.error(err);
      setDaySupplements((prev) =>
        prev.map((item) => (item.id === dose.id ? { ...item, is_taken: dose.is_taken } : item))
      );
      setError("Could not update supplement status.");
    }
  };

  const loadDayWorkout = async (date) => {
    try {
      setWorkoutLoading(true);
      const dateStr = formatDate(date);
      const res = await api.post("/api/workout-day-logs/ensure-for-date/", { date: dateStr });
      setWorkoutMeta({
        rest_day: !!res.data.rest_day,
        plan: res.data.plan || null,
        session: res.data.session || null,
        message: res.data.message || "",
      });
      setWorkoutDayLog(res.data.day_log || null);
    } catch (err) {
      console.error("Failed to load day workout:", err);
      setWorkoutDayLog(null);
      setWorkoutMeta({ rest_day: false, plan: null, session: null, message: "" });
    } finally {
      setWorkoutLoading(false);
    }
  };

  const updateSetLogLocally = (setId, patch) => {
    setWorkoutDayLog((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        set_logs: (prev.set_logs || []).map((item) =>
          item.id === setId ? { ...item, ...patch } : item
        ),
      };
    });
  };

  const handleToggleSetCompleted = async (setLog) => {
    const is_completed = !setLog.is_completed;
    updateSetLogLocally(setLog.id, { is_completed });
    try {
      await api.patch(`/api/workout-set-logs/${setLog.id}/`, { is_completed });
      setWorkoutDayLog((prev) => {
        if (!prev) return prev;
        const set_logs = (prev.set_logs || []).map((item) =>
          item.id === setLog.id ? { ...item, is_completed } : item
        );
        return {
          ...prev,
          set_logs,
          completed_sets: set_logs.filter((s) => s.is_completed).length,
          total_sets: set_logs.length,
        };
      });
    } catch (err) {
      console.error(err);
      updateSetLogLocally(setLog.id, { is_completed: setLog.is_completed });
      setError("Could not update set.");
    }
  };

  const handleSetFieldChange = (setId, field, value) => {
    updateSetLogLocally(setId, { [field]: value });
  };

  const handleSetFieldBlur = async (setId) => {
    const setLog = (workoutDayLog?.set_logs || []).find((item) => item.id === setId);
    if (!setLog) return;
    try {
      await api.patch(`/api/workout-set-logs/${setId}/`, {
        weight_kg: setLog.weight_kg === "" || setLog.weight_kg == null ? null : Number(setLog.weight_kg),
        reps: setLog.reps === "" || setLog.reps == null ? null : Number(setLog.reps),
      });
    } catch (err) {
      console.error(err);
      setError("Could not save set.");
    }
  };

  const handleToggleWorkoutCompleted = async () => {
    if (!workoutDayLog) return;
    const is_completed = !workoutDayLog.is_completed;
    setWorkoutDayLog((prev) => (prev ? { ...prev, is_completed } : prev));
    try {
      await api.patch(`/api/workout-day-logs/${workoutDayLog.id}/`, { is_completed });
    } catch (err) {
      console.error(err);
      setWorkoutDayLog((prev) => (prev ? { ...prev, is_completed: !is_completed } : prev));
      setError("Could not update workout completion.");
    }
  };

  const workoutExercisesGrouped = (() => {
    const logs = workoutDayLog?.set_logs || [];
    const byExercise = new Map();
    logs.forEach((setLog) => {
      const key = setLog.exercise;
      if (!byExercise.has(key)) {
        byExercise.set(key, {
          exerciseId: key,
          name: setLog.exercise_name,
          section: setLog.exercise_section,
          muscle_group: setLog.exercise_muscle_group,
          order: setLog.exercise_order,
          target_sets: setLog.target_sets,
          target_reps: setLog.target_reps,
          target_weight_kg: setLog.target_weight_kg,
          rest_seconds: setLog.rest_seconds,
          duration_minutes: setLog.duration_minutes,
          sets: [],
        });
      }
      byExercise.get(key).sets.push(setLog);
    });
    const list = Array.from(byExercise.values());
    list.forEach((ex) => ex.sets.sort((a, b) => a.set_number - b.set_number));
    list.sort((a, b) => {
      if (a.section !== b.section) return a.section === "warmup" ? -1 : 1;
      return (a.order || 0) - (b.order || 0);
    });
    return list;
  })();

  const nutritionTotals = dayMeals.reduce(
    (acc, meal) => ({
      calories: acc.calories + Number(meal.calories || 0),
      protein_g: acc.protein_g + Number(meal.protein_g || 0),
      carbs_g: acc.carbs_g + Number(meal.carbs_g || 0),
      fat_g: acc.fat_g + Number(meal.fat_g || 0),
    }),
    { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }
  );
  const eatenTotals = dayMeals
    .filter((meal) => meal.is_eaten)
    .reduce(
      (acc, meal) => ({
        calories: acc.calories + Number(meal.calories || 0),
        protein_g: acc.protein_g + Number(meal.protein_g || 0),
        carbs_g: acc.carbs_g + Number(meal.carbs_g || 0),
        fat_g: acc.fat_g + Number(meal.fat_g || 0),
      }),
      { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }
    );
  const eatenCount = dayMeals.filter((meal) => meal.is_eaten).length;
  const takenSupplementCount = daySupplements.filter((dose) => dose.is_taken).length;

  const formatMacro = (value) => Math.round(Number(value) || 0);

  const addTaskToPlan = async (section, title) => {
    if (!title || !plan || saving) return;

    try {
      setSaving(true);
      setError(null);
      const response = await api.post("/api/day-plan-tasks/", {
        plan: plan.id,
        title,
        section,
      });
      setPlan((prev) => ({
        ...prev,
        tasks: [...(prev.tasks || []), response.data],
      }));

      const exists = presets.some(
        (preset) =>
          preset.section === section &&
          preset.title.toLowerCase() === title.toLowerCase()
      );
      if (!exists) {
        try {
          const presetRes = await api.post("/api/routine-presets/", {
            title,
            section,
          });
          setPresets((prev) => [...prev, presetRes.data]);
        } catch (err) {
          // Preset may already exist — ignore
        }
      }
    } catch (err) {
      console.error("Failed to add task:", err);
      setError("Could not add task.");
    } finally {
      setSaving(false);
    }
  };

  const handlePresetClick = async (preset) => {
    if (addedTitles.has(preset.title.toLowerCase())) return;
    await addTaskToPlan(preset.section, preset.title);
  };

  const handleDeletePreset = async (presetId) => {
    try {
      await api.delete(`/api/routine-presets/${presetId}/`);
      setPresets((prev) => prev.filter((preset) => preset.id !== presetId));
    } catch (err) {
      console.error("Failed to delete preset:", err);
      setError("Could not remove saved item.");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      setError(null);
      await api.delete(`/api/day-plan-tasks/${taskId}/`);
      setPlan((prev) => ({
        ...prev,
        tasks: (prev.tasks || []).filter((task) => task.id !== taskId),
      }));
    } catch (err) {
      console.error("Failed to delete task:", err);
      setError("Could not delete task.");
    }
  };

  const updateTaskLocally = (taskId, updates) => {
    setPlan((prev) => ({
      ...prev,
      tasks: (prev.tasks || []).map((task) =>
        task.id === taskId ? { ...task, ...updates } : task
      ),
    }));
  };

  const handleToggleCompleted = async (task) => {
    const is_completed = !task.is_completed;
    updateTaskLocally(task.id, { is_completed });
    try {
      await api.patch(`/api/day-plan-tasks/${task.id}/`, { is_completed });
    } catch (err) {
      console.error("Failed to update task:", err);
      updateTaskLocally(task.id, { is_completed: task.is_completed });
      setError("Could not update task.");
    }
  };

  const handleResultChange = (taskId, result) => {
    updateTaskLocally(taskId, { result });
  };

  const handleWeightChange = (taskId, value) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      updateTaskLocally(taskId, { result: value });
    }
  };

  const handleResultBlur = async (task) => {
    let result = task.result || "";

    if (needsResult(task.title) && result !== "") {
      const weight = parseFloat(result);
      if (Number.isNaN(weight) || weight < 35 || weight > 200) {
        setError("Weight must be a number between 35 and 200.");
        updateTaskLocally(task.id, { result: "" });
        return;
      }
      result = String(weight);
      updateTaskLocally(task.id, { result });
    }

    try {
      await api.patch(`/api/day-plan-tasks/${task.id}/`, { result });
    } catch (err) {
      console.error("Failed to save result:", err);
      setError("Could not save result.");
    }
  };

  const openPresetModal = (section) => {
    setPresetSection(section);
    setPresetSearch("");
    setShowPresetModal(true);
  };

  const handleSelectRelaxation = async (title) => {
    if (!plan) return;
    const previous = plan.relaxation || "";
    const previousCompleted = !!plan.relaxation_completed;
    setPlan((prev) => ({ ...prev, relaxation: title, relaxation_completed: false }));
    try {
      await api.patch(`/api/day-plans/${plan.id}/`, {
        relaxation: title,
        relaxation_completed: false,
      });
    } catch (err) {
      console.error("Failed to save relaxation:", err);
      setPlan((prev) => ({
        ...prev,
        relaxation: previous,
        relaxation_completed: previousCompleted,
      }));
      setError("Could not save relaxation choice.");
    }
  };

  const handleToggleRelaxationCompleted = async () => {
    if (!plan) return;
    const relaxation_completed = !plan.relaxation_completed;
    setPlan((prev) => ({ ...prev, relaxation_completed }));
    try {
      await api.patch(`/api/day-plans/${plan.id}/`, { relaxation_completed });
    } catch (err) {
      console.error("Failed to save relaxation completion:", err);
      setPlan((prev) => ({ ...prev, relaxation_completed: !relaxation_completed }));
      setError("Could not update relaxation.");
    }
  };

  const handleAddRelaxationOption = async (title) => {
    const exists = relaxationPresets.some(
      (preset) => preset.title.toLowerCase() === title.toLowerCase()
    );
    if (exists) {
      await handleSelectRelaxation(title);
      return;
    }
    try {
      setSaving(true);
      const response = await api.post("/api/routine-presets/", {
        title,
        section: "relaxation",
      });
      setPresets((prev) => [...prev, response.data]);
      await handleSelectRelaxation(title);
    } catch (err) {
      console.error("Failed to add relaxation option:", err);
      setError("Could not add relaxation method.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRelaxationOption = async (presetId) => {
    const option = relaxationPresets.find((preset) => preset.id === presetId);
    try {
      await api.delete(`/api/routine-presets/${presetId}/`);
      setPresets((prev) => prev.filter((preset) => preset.id !== presetId));
      if (option && plan?.relaxation === option.title) {
        setPlan((prev) => ({ ...prev, relaxation: "", relaxation_completed: false }));
        if (plan?.id) {
          await api.patch(`/api/day-plans/${plan.id}/`, {
            relaxation: "",
            relaxation_completed: false,
          });
        }
      }
    } catch (err) {
      console.error("Failed to delete relaxation option:", err);
      setError("Could not remove relaxation method.");
    }
  };

  const handleJournalChange = (value) => {
    setJournal(value);
    setJournalDirty(true);
  };

  const handleExportPdf = async () => {
    try {
      setPdfExporting(true);
      setError(null);
      const dateStr = formatDate(selectedDate);
      const response = await api.get("/api/generate-day-pdf/", {
        params: { date: dateStr },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `day-${dateStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to export day PDF:", err);
      const status = err.response?.status;
      if (status === 404) {
        setError("PDF export endpoint not found — restart the backend server and try again.");
      } else {
        setError("Could not export PDF for this day.");
      }
    } finally {
      setPdfExporting(false);
    }
  };

  const handleSaveJournal = async () => {
    if (!plan) return;
    try {
      setJournalSaving(true);
      setError(null);
      await api.patch(`/api/day-plans/${plan.id}/`, { journal });
      setPlan((prev) => ({ ...prev, journal }));
      setJournalDirty(false);
      setJournalSavedAt(new Date());
    } catch (err) {
      console.error("Failed to save journal:", err);
      setError("Could not save journal.");
    } finally {
      setJournalSaving(false);
    }
  };

  return (
    <Container fluid>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div className="d-flex align-items-center">
          <FaCalendarDay className="me-2" style={{ color: SIDEBAR_BLUE, fontSize: "1.5rem" }} />
          <h2 className="mb-0" style={{ color: "#fff" }}>Day Builder</h2>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline-info"
            disabled={pdfExporting}
            onClick={handleExportPdf}
            title="Download printable PDF for this day"
          >
            <FaFilePdf className="me-1" />
            {pdfExporting ? "Exporting…" : "Export PDF"}
          </Button>
          <div className="d-flex align-items-center gap-1">
          <Button
            variant="link"
            size="sm"
            title="Previous month"
            onClick={() => shiftDate({ months: -1 })}
            style={{ color: SIDEBAR_BLUE, textDecoration: "none", padding: "4px 6px" }}
          >
            <FaAngleDoubleLeft />
          </Button>
          <Button
            variant="link"
            size="sm"
            title="Previous day"
            onClick={() => shiftDate({ days: -1 })}
            style={{ color: SIDEBAR_BLUE, textDecoration: "none", padding: "4px 6px" }}
          >
            <FaChevronLeft />
          </Button>

          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            calendarClassName="day-builder-calendar"
            customInput={
              <Form.Control
                style={{
                  backgroundColor: "#2d2d2d",
                  border: `1px solid ${SIDEBAR_BLUE}`,
                  color: "#fff",
                  width: "140px",
                  cursor: "pointer",
                  textAlign: "center",
                }}
              />
            }
          />

          <Button
            variant="link"
            size="sm"
            title="Next day"
            onClick={() => shiftDate({ days: 1 })}
            style={{ color: SIDEBAR_BLUE, textDecoration: "none", padding: "4px 6px" }}
          >
            <FaChevronRight />
          </Button>
          <Button
            variant="link"
            size="sm"
            title="Next month"
            onClick={() => shiftDate({ months: 1 })}
            style={{ color: SIDEBAR_BLUE, textDecoration: "none", padding: "4px 6px" }}
          >
            <FaAngleDoubleRight />
          </Button>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: SIDEBAR_BLUE }} />
        </div>
      ) : (
        <>
        <Row>
          <Col lg={4}>
            <RoutineSection
              title="Morning Routine"
              icon={<FaSun className="me-2" style={{ color: "#FBBF24" }} />}
              description="Pick from your saved list, or type a new item."
              section="morning"
              tasks={morningTasks}
              presets={morningPresets}
              saving={saving}
              onAddTask={addTaskToPlan}
              onDeleteTask={handleDeleteTask}
              onToggleCompleted={handleToggleCompleted}
              onResultChange={handleResultChange}
              onWeightChange={handleWeightChange}
              onResultBlur={handleResultBlur}
              onOpenPresets={openPresetModal}
            />

            {showWork && (
              <>
                <RoutineSection
                  title="Work (Travel Zone)"
                  icon={<FaBriefcase className="me-2" style={{ color: SIDEBAR_BLUE }} />}
                  description="Office-day tasks — commute, meetings, and everything in between."
                  section="work"
                  tasks={workTasks}
                  presets={workPresets}
                  saving={saving}
                  onAddTask={addTaskToPlan}
                  onDeleteTask={handleDeleteTask}
                  onToggleCompleted={handleToggleCompleted}
                  onResultChange={handleResultChange}
                  onWeightChange={handleWeightChange}
                  onResultBlur={handleResultBlur}
                  onOpenPresets={openPresetModal}
                />

                <Card
                  className="mb-4"
                  style={{
                    backgroundColor: "#1e1e1e",
                    border: `1px solid ${SIDEBAR_BLUE}`,
                  }}
                >
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                      <div>
                        <h5 style={{ color: "#fff", marginBottom: 4 }}>
                          <FaDumbbell className="me-2" style={{ color: "#F97316" }} />
                          Workout session
                        </h5>
                        {workoutMeta.session && (
                          <div style={{ color: "#d1d5db", fontSize: "0.9rem" }}>
                            {workoutMeta.session.name || "Today's workout"}
                            {workoutMeta.plan ? ` · ${workoutMeta.plan.name}` : ""}
                          </div>
                        )}
                      </div>
                      <Link
                        to={`/workout?date=${formatDate(selectedDate)}`}
                        style={{ textDecoration: "none" }}
                      >
                        <Button size="sm" variant="outline-info">
                          Open planner
                          <FaArrowRight className="ms-2" />
                        </Button>
                      </Link>
                    </div>

                    {workoutLoading ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" size="sm" style={{ color: SIDEBAR_BLUE }} />
                      </div>
                    ) : !workoutMeta.plan ? (
                      <p className="mb-0" style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
                        No workout plan covers this day yet.
                      </p>
                    ) : workoutMeta.rest_day ? (
                      <p className="mb-0" style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
                        No workout scheduled for this date.
                      </p>
                    ) : !workoutDayLog ? (
                      <p className="mb-0" style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
                        No exercises on this day yet.
                      </p>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                          <span style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                            Done {workoutDayLog.completed_sets || 0} / {workoutDayLog.total_sets || 0}
                          </span>
                          <Form.Check
                            type="checkbox"
                            checked={!!workoutDayLog.is_completed}
                            onChange={handleToggleWorkoutCompleted}
                            label={
                              <span style={{ color: "#fff", fontSize: "0.85rem" }}>
                                Mark workout done
                              </span>
                            }
                          />
                        </div>

                        {workoutExercisesGrouped.map((exercise) => (
                          <div
                            key={exercise.exerciseId}
                            className="mb-3"
                            style={{
                              backgroundColor: "#2d2d2d",
                              border: `1px solid ${SIDEBAR_BLUE}`,
                              borderRadius: 4,
                              padding: "12px 14px",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                              <div>
                                <div style={{ color: "#fff", fontWeight: 600 }}>
                                  {exercise.section === "warmup" && (
                                    <span style={{ color: "#FBBF24", fontSize: "0.75rem", marginRight: 6 }}>
                                      WARM-UP
                                    </span>
                                  )}
                                  {exercise.name}
                                </div>
                                <div style={{ color: "#9ca3af", fontSize: "0.8rem" }}>
                                  {exercise.duration_minutes
                                    ? `Plan: ${exercise.duration_minutes} min`
                                    : `Plan: ${exercise.target_sets}×${exercise.target_reps}${
                                        exercise.target_weight_kg != null
                                          ? ` @ ${exercise.target_weight_kg}kg`
                                          : ""
                                      }${
                                        exercise.rest_seconds != null
                                          ? ` · rest ${exercise.rest_seconds}s`
                                          : ""
                                      }`}
                                </div>
                              </div>
                            </div>

                            {exercise.duration_minutes ? (
                              (exercise.sets || []).map((setLog) => (
                                <Form.Check
                                  key={setLog.id}
                                  type="checkbox"
                                  className="mb-1"
                                  checked={!!setLog.is_completed}
                                  onChange={() => handleToggleSetCompleted(setLog)}
                                  label={
                                    <span style={{
                                      color: "#d1d5db",
                                      fontSize: "0.85rem",
                                      textDecoration: setLog.is_completed ? "line-through" : "none",
                                    }}>
                                      Done ({exercise.duration_minutes} min)
                                    </span>
                                  }
                                />
                              ))
                            ) : (
                              (exercise.sets || []).map((setLog) => (
                                <div
                                  key={setLog.id}
                                  className="d-flex align-items-center flex-wrap gap-2 mb-2"
                                >
                                  <Form.Check
                                    type="checkbox"
                                    checked={!!setLog.is_completed}
                                    onChange={() => handleToggleSetCompleted(setLog)}
                                    label={
                                      <span style={{
                                        color: "#d1d5db",
                                        fontSize: "0.85rem",
                                        minWidth: 48,
                                        textDecoration: setLog.is_completed ? "line-through" : "none",
                                      }}>
                                        Set {setLog.set_number}
                                      </span>
                                    }
                                  />
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={setLog.weight_kg ?? ""}
                                    onChange={(e) =>
                                      handleSetFieldChange(setLog.id, "weight_kg", e.target.value)
                                    }
                                    onBlur={() => handleSetFieldBlur(setLog.id)}
                                    placeholder="kg"
                                    style={{
                                      backgroundColor: "#1e1e1e",
                                      border: `1px solid ${SIDEBAR_BLUE}`,
                                      color: "#fff",
                                      width: 80,
                                      fontSize: "0.85rem",
                                    }}
                                  />
                                  <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>kg ×</span>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={setLog.reps ?? ""}
                                    onChange={(e) =>
                                      handleSetFieldChange(setLog.id, "reps", e.target.value)
                                    }
                                    onBlur={() => handleSetFieldBlur(setLog.id)}
                                    placeholder="reps"
                                    style={{
                                      backgroundColor: "#1e1e1e",
                                      border: `1px solid ${SIDEBAR_BLUE}`,
                                      color: "#fff",
                                      width: 70,
                                      fontSize: "0.85rem",
                                    }}
                                  />
                                  <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>reps</span>
                                </div>
                              ))
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </Card.Body>
                </Card>
              </>
            )}

            <RoutineSection
              title="Day Core"
              icon={<FaBolt className="me-2" style={{ color: "#A78BFA" }} />}
              description="Your main block — customers, food, mental work, and more."
              section="day_core"
              tasks={dayCoreTasks}
              presets={dayCorePresets}
              saving={saving}
              onAddTask={addTaskToPlan}
              onDeleteTask={handleDeleteTask}
              onToggleCompleted={handleToggleCompleted}
              onResultChange={handleResultChange}
              onWeightChange={handleWeightChange}
              onResultBlur={handleResultBlur}
              onOpenPresets={openPresetModal}
              extraContent={
                <RelaxationPicker
                  options={relaxationPresets}
                  selected={plan?.relaxation || ""}
                  completed={!!plan?.relaxation_completed}
                  saving={saving}
                  onSelect={handleSelectRelaxation}
                  onToggleCompleted={handleToggleRelaxationCompleted}
                  onAddOption={handleAddRelaxationOption}
                  onDeleteOption={handleDeleteRelaxationOption}
                />
              }
            />
          </Col>

          <Col lg={4}>
            <Card
              className="mb-4"
              style={{
                backgroundColor: "#1e1e1e",
                border: `1px solid ${SIDEBAR_BLUE}`,
              }}
            >
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <FaLeaf className="me-2" style={{ color: DO_GREEN }} />
                  <h4 className="mb-0" style={{ color: "#fff" }}>Daily Compass</h4>
                </div>
                <p className="mb-4" style={{ fontSize: "0.9rem", color: "#d1d5db" }}>
                  Optional boosts to aim for, and traps to stay away from.
                </p>

                <GuidelineGroup
                  heading="Do (optional)"
                  icon={<FaCheckCircle className="me-2" style={{ color: DO_GREEN }} />}
                  accent={DO_GREEN}
                  hint="Nice-to-haves to aim for today."
                  section="optional"
                  tasks={optionalTasks}
                  presets={optionalPresets}
                  saving={saving}
                  onDeleteTask={handleDeleteTask}
                  onOpenPresets={openPresetModal}
                  onAddTask={addTaskToPlan}
                />

                <GuidelineGroup
                  heading="Don't"
                  icon={<FaBan className="me-2" style={{ color: DONT_RED }} />}
                  accent={DONT_RED}
                  hint="Traps to stay away from today."
                  section="avoid"
                  tasks={avoidTasks}
                  presets={avoidPresets}
                  saving={saving}
                  onDeleteTask={handleDeleteTask}
                  onOpenPresets={openPresetModal}
                  onAddTask={addTaskToPlan}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <Card
              className="mb-4"
              style={{
                backgroundColor: "#1e1e1e",
                border: `1px solid ${SIDEBAR_BLUE}`,
              }}
            >
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <FaUtensils className="me-2" style={{ color: "#FBBF24" }} />
                  <h4 className="mb-0" style={{ color: "#fff" }}>Day's Nutrition</h4>
                </div>

                <p className="mb-3" style={{ fontSize: "0.9rem", color: "#d1d5db" }}>
                  Meals planned for this day. Check each one when you eat it.
                </p>

                {nutritionLoading ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" style={{ color: SIDEBAR_BLUE }} />
                  </div>
                ) : dayMeals.length === 0 ? (
                  <p className="mb-3" style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
                    No nutrition plan for this day yet.
                  </p>
                ) : (
                  <>
                    <div className="mb-2" style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                      Eaten {eatenCount} / {dayMeals.length}
                    </div>

                    {dayMeals.map((meal) => (
                      <div
                        key={meal.id}
                        className="mb-3"
                        style={{
                          backgroundColor: "#2d2d2d",
                          border: `1px solid ${meal.is_eaten ? DO_GREEN : SIDEBAR_BLUE}`,
                          borderRadius: "4px",
                          padding: "12px 14px",
                          opacity: meal.is_eaten ? 0.85 : 1,
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <Form.Check
                            type="checkbox"
                            checked={!!meal.is_eaten}
                            onChange={() => handleToggleMealEaten(meal)}
                            label={
                              <span style={{
                                color: "#fff",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                textDecoration: meal.is_eaten ? "line-through" : "none",
                              }}>
                                {(meal.time || "").slice(0, 5)}
                                {" · "}
                                {meal.name || meal.meal_type}
                              </span>
                            }
                          />
                          <span style={{ color: "#FBBF24", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                            {formatMacro(meal.calories)} kcal
                          </span>
                        </div>

                        {(meal.items || []).length > 0 && (
                          <ul className="mb-2 ps-3" style={{ color: "#d1d5db", fontSize: "0.8rem" }}>
                            {meal.items.map((item) => (
                              <li key={item.id}>
                                {item.food_name
                                  ? `${item.quantity_g}g ${item.food_name}`
                                  : item.recipe_name}
                              </li>
                            ))}
                          </ul>
                        )}

                        <div
                          className="d-flex flex-wrap gap-3"
                          style={{ color: "#9ca3af", fontSize: "0.8rem" }}
                        >
                          <span>P {formatMacro(meal.protein_g)}g</span>
                          <span>C {formatMacro(meal.carbs_g)}g</span>
                          <span>F {formatMacro(meal.fat_g)}g</span>
                        </div>
                      </div>
                    ))}

                    <div
                      className="mb-3"
                      style={{
                        backgroundColor: "#1a2738",
                        border: `1px solid ${SIDEBAR_BLUE}`,
                        borderRadius: "4px",
                        padding: "12px 14px",
                      }}
                    >
                      <div className="mb-2" style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
                        Day total
                      </div>
                      <div className="d-flex justify-content-between mb-1" style={{ color: "#d1d5db", fontSize: "0.85rem" }}>
                        <span>Calories</span>
                        <span style={{ color: "#FBBF24" }}>{formatMacro(nutritionTotals.calories)} kcal</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1" style={{ color: "#d1d5db", fontSize: "0.85rem" }}>
                        <span>Protein</span>
                        <span style={{ color: "#fff" }}>{formatMacro(nutritionTotals.protein_g)}g</span>
                      </div>
                      <div className="d-flex justify-content-between mb-1" style={{ color: "#d1d5db", fontSize: "0.85rem" }}>
                        <span>Carbs</span>
                        <span style={{ color: "#fff" }}>{formatMacro(nutritionTotals.carbs_g)}g</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2" style={{ color: "#d1d5db", fontSize: "0.85rem" }}>
                        <span>Fat</span>
                        <span style={{ color: "#fff" }}>{formatMacro(nutritionTotals.fat_g)}g</span>
                      </div>
                      {eatenCount > 0 && (
                        <div style={{ color: "#9ca3af", fontSize: "0.75rem", borderTop: "1px solid #374151", paddingTop: 8 }}>
                          Eaten so far: {formatMacro(eatenTotals.calories)} kcal ·
                          P {formatMacro(eatenTotals.protein_g)}g ·
                          C {formatMacro(eatenTotals.carbs_g)}g ·
                          F {formatMacro(eatenTotals.fat_g)}g
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div
                  className="mb-3"
                  style={{
                    backgroundColor: "#2d2d2d",
                    border: `1px solid ${SIDEBAR_BLUE}`,
                    borderRadius: "4px",
                    padding: "12px 14px",
                  }}
                >
                  <div className="mb-2" style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
                    Supplements
                  </div>
                  <div className="d-flex justify-content-between mb-2" style={{ color: "#d1d5db", fontSize: "0.85rem" }}>
                    <span>Taken</span>
                    <span style={{ color: "#fff" }}>
                      {takenSupplementCount} / {daySupplements.length}
                    </span>
                  </div>

                  {daySupplements.length === 0 ? (
                    <p className="mb-0" style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                      No supplements scheduled for this day.
                    </p>
                  ) : (
                    daySupplements.map((dose) => (
                      <div
                        key={dose.id}
                        className="mb-2"
                        style={{
                          borderTop: "1px solid #374151",
                          paddingTop: 8,
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={!!dose.is_taken}
                          onChange={() => handleToggleSupplementTaken(dose)}
                          label={
                            <span style={{
                              color: "#fff",
                              fontSize: "0.85rem",
                              textDecoration: dose.is_taken ? "line-through" : "none",
                              opacity: dose.is_taken ? 0.75 : 1,
                            }}>
                              {(dose.time || "").slice(0, 5)}
                              {" · "}
                              {dose.name}
                              {dose.dosage ? ` (${dose.dosage})` : ""}
                            </span>
                          }
                        />
                      </div>
                    ))
                  )}
                </div>

                <Link
                  to={`/nutrition?date=${formatDate(selectedDate)}`}
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    style={{
                      backgroundColor: SIDEBAR_BLUE,
                      borderColor: SIDEBAR_BLUE,
                      width: "100%",
                    }}
                  >
                    Open nutrition planner
                    <FaArrowRight className="ms-2" />
                  </Button>
                </Link>
              </Card.Body>
            </Card>

            <RoutineSection
              title="Day's Exposure"
              icon={<FaEye className="me-2" style={{ color: "#F472B6" }} />}
              description="Phobia exposures for today — add tasks and check them off when done."
              section="exposure"
              tasks={exposureTasks}
              presets={exposurePresets}
              saving={saving}
              onAddTask={addTaskToPlan}
              onDeleteTask={handleDeleteTask}
              onToggleCompleted={handleToggleCompleted}
              onResultChange={handleResultChange}
              onWeightChange={handleWeightChange}
              onResultBlur={handleResultBlur}
              onOpenPresets={openPresetModal}
            />
          </Col>
        </Row>

        <Card
          className="mb-4"
          style={{
            backgroundColor: "#1e1e1e",
            border: `1px solid ${SIDEBAR_BLUE}`,
          }}
        >
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div className="d-flex align-items-center">
                <FaBook className="me-2" style={{ color: "#FBBF24" }} />
                <h4 className="mb-0" style={{ color: "#fff" }}>Day Journal</h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
                  {journalSaving
                    ? "Saving..."
                    : journalDirty
                      ? "Unsaved changes"
                      : journalSavedAt
                        ? "Saved"
                        : plan?.journal
                          ? "Loaded"
                          : ""}
                </span>
                <Button
                  onClick={handleSaveJournal}
                  disabled={!plan || journalSaving || !journalDirty}
                  style={{
                    backgroundColor: SIDEBAR_BLUE,
                    borderColor: SIDEBAR_BLUE,
                  }}
                >
                  <FaSave className="me-2" />
                  Save journal
                </Button>
              </div>
            </div>

            <p className="mb-3" style={{ fontSize: "0.9rem", color: "#d1d5db" }}>
              End-of-day notes for this date. Use bold, lists, and more — each day has its own journal.
            </p>

            <div className="day-builder-journal">
              <ReactQuill
                theme="snow"
                value={journal}
                onChange={handleJournalChange}
                modules={JOURNAL_MODULES}
                formats={JOURNAL_FORMATS}
                placeholder="How did today go?"
              />
            </div>
          </Card.Body>
        </Card>
        </>
      )}

      <Modal
        show={showPresetModal}
        onHide={() => setShowPresetModal(false)}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ borderBottom: `1px solid ${SIDEBAR_BLUE}`, backgroundColor: "#1e1e1e" }}
        >
          <Modal.Title style={{ color: "#fff" }}>
            {SECTION_LABELS[presetSection] || "Saved items"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: "#1e1e1e" }}>
          <Form.Control
            type="text"
            className="day-builder-input mb-3"
            placeholder="Search saved items..."
            value={presetSearch}
            onChange={(e) => setPresetSearch(e.target.value)}
            style={{
              backgroundColor: "#2d2d2d",
              border: `1px solid ${SIDEBAR_BLUE}`,
              color: "#fff",
            }}
          />

          {filteredPresets.length === 0 ? (
            <p className="mb-0" style={{ color: "#d1d5db" }}>
              No saved items match your search.
            </p>
          ) : (
            <ListGroup
              variant="flush"
              style={{ maxHeight: "360px", overflowY: "auto" }}
            >
              {filteredPresets.map((preset) => {
                const alreadyAdded = addedTitles.has(preset.title.toLowerCase());
                return (
                  <ListGroup.Item
                    key={preset.id}
                    className="d-flex justify-content-between align-items-center"
                    style={{
                      backgroundColor: "#2d2d2d",
                      border: `1px solid ${SIDEBAR_BLUE}`,
                      borderRadius: "4px",
                      marginBottom: "8px",
                      color: "#fff",
                    }}
                  >
                    <span style={{ color: alreadyAdded ? "#9ca3af" : "#fff" }}>
                      {preset.title}
                      {alreadyAdded && (
                        <span className="ms-2" style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                          (added)
                        </span>
                      )}
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      <Button
                        size="sm"
                        disabled={alreadyAdded || saving}
                        onClick={() => handlePresetClick(preset)}
                        style={{
                          backgroundColor: alreadyAdded ? "#374151" : SIDEBAR_BLUE,
                          borderColor: alreadyAdded ? "#374151" : SIDEBAR_BLUE,
                        }}
                      >
                        <FaPlus className="me-1" />
                        Add
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleDeletePreset(preset.id)}
                        style={{ color: "#F87171", textDecoration: "none" }}
                        title="Remove from saved list"
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${SIDEBAR_BLUE}`, backgroundColor: "#1e1e1e" }}>
          <Button
            variant="secondary"
            onClick={() => setShowPresetModal(false)}
            style={{ backgroundColor: "#374151", borderColor: "#374151" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DayBuilder;
